import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import '../styles/DesignSafety.css';
import { translations } from '../translations';

const DesignSafety = ({ language }) => {
  const t = translations[language];
  const [selectedCard, setSelectedCard] = useState(null);
  const [mounted, setMounted] = useState(false);
  const isKannada = language === 'kannada';
  const readMoreText = isKannada ? 'ಇನ್ನಷ್ಟು ಓದಿ…' : 'Read more…';

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    document.body.style.overflow = selectedCard !== null ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [selectedCard]);

  useEffect(() => {
    if (!selectedCard) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') setSelectedCard(null);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [selectedCard]);

  const handleCloseModal = useCallback((e) => {
    if (e.target === e.currentTarget || e.target.closest('.design-modal-close')) {
      setSelectedCard(null);
    }
  }, []);
  
  const features = useMemo(() => [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="6" cy="19" r="3"></circle>
          <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"></path>
          <circle cx="18" cy="5" r="3"></circle>
        </svg>
      ),
      title: typeof t.design.features.expressway === 'object' ? t.design.features.expressway.heading : t.design.features.expressway,
      description: typeof t.design.features.expressway === 'object' ? t.design.features.expressway.content : t.design.features.expresswayDesc,
      readMoreContent: typeof t.design.features.expressway === 'object' ? t.design.features.expressway.readMoreContent : null
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m17 14 3 3.3a1 1 0 0 1-.7 1.7H4.7a1 1 0 0 1-.7-1.7L7 14h-.3a1 1 0 0 1-.7-1.7L9 9h-.2A1 1 0 0 1 8 7.3L12 3l4 4.3a1 1 0 0 1-.8 1.7H15l3 3.3a1 1 0 0 1-.7 1.7H17Z"></path>
          <path d="M12 22v-3"></path>
        </svg>
      ),
      title: typeof t.design.features.median === 'object' ? t.design.features.median.heading : t.design.features.median,
      description: typeof t.design.features.median === 'object' ? t.design.features.median.content : t.design.features.medianDesc,
      readMoreContent: typeof t.design.features.median === 'object' ? t.design.features.median.readMoreContent : null
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 8h14"></path>
          <path d="M5 12h14"></path>
          <path d="M5 16h14"></path>
          <path d="M3 3v18"></path>
          <path d="M21 3v18"></path>
        </svg>
      ),
      title: typeof t.design.features.serviceRoads === 'object' ? t.design.features.serviceRoads.heading : t.design.features.serviceRoads,
      description: typeof t.design.features.serviceRoads === 'object' ? t.design.features.serviceRoads.content : t.design.features.serviceRoadsDesc,
      readMoreContent: typeof t.design.features.serviceRoads === 'object' ? t.design.features.serviceRoads.readMoreContent : null
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="18.5" cy="17.5" r="3.5"></circle>
          <circle cx="5.5" cy="17.5" r="3.5"></circle>
          <circle cx="15" cy="5" r="1"></circle>
          <path d="M12 17.5V14l-3-3 4-3 2 3h2"></path>
        </svg>
      ),
      title: typeof t.design.features.cycleTracks === 'object' ? t.design.features.cycleTracks.heading : t.design.features.cycleTracks,
      description: typeof t.design.features.cycleTracks === 'object' ? t.design.features.cycleTracks.content : t.design.features.cycleTracksDesc,
      readMoreContent: typeof t.design.features.cycleTracks === 'object' ? t.design.features.cycleTracks.readMoreContent : null
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 16v-2.38C4 11.5 2.97 10.5 3 8c.03-2.72 1.49-6 4.5-6C9.37 2 10 3.8 10 5.5c0 3.11-2 5.66-2 8.68V16a2 2 0 1 1-4 0Z"></path>
          <path d="M20 20v-2.38c0-2.12 1.03-3.12 1-5.62-.03-2.72-1.49-6-4.5-6C14.63 6 14 7.8 14 9.5c0 3.11 2 5.66 2 8.68V20a2 2 0 1 0 4 0Z"></path>
          <path d="M16 17h4"></path>
          <path d="M4 13h4"></path>
        </svg>
      ),
      title: typeof t.design.features.footpaths === 'object' ? t.design.features.footpaths.heading : t.design.features.footpaths,
      description: typeof t.design.features.footpaths === 'object' ? t.design.features.footpaths.content : t.design.features.footpathsDesc,
      readMoreContent: typeof t.design.features.footpaths === 'object' ? t.design.features.footpaths.readMoreContent : null
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"></path>
        </svg>
      ),
      title: typeof t.design.features.utilities === 'object' ? t.design.features.utilities.heading : t.design.features.utilities,
      description: typeof t.design.features.utilities === 'object' ? t.design.features.utilities.content : t.design.features.utilitiesDesc,
      readMoreContent: typeof t.design.features.utilities === 'object' ? t.design.features.utilities.readMoreContent : null
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
        </svg>
      ),
      title: typeof t.design.features.safety === 'object' ? t.design.features.safety.heading : t.design.features.safety,
      description: typeof t.design.features.safety === 'object' ? t.design.features.safety.content : t.design.features.safetyDesc,
      readMoreContent: typeof t.design.features.safety === 'object' ? t.design.features.safety.readMoreContent : null
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 13v8"></path>
          <path d="M12 3v3"></path>
          <path d="M18 6a2 2 0 0 1 1.387.56l2.307 2.22a1 1 0 0 1 0 1.44l-2.307 2.22A2 2 0 0 1 18 13H6a2 2 0 0 1-1.387-.56l-2.306-2.22a1 1 0 0 1 0-1.44l2.306-2.22A2 2 0 0 1 6 6z"></path>
        </svg>
      ),
      title: typeof t.design.features.signboards === 'object' ? t.design.features.signboards.heading : t.design.features.signboards,
      description: typeof t.design.features.signboards === 'object' ? t.design.features.signboards.content : t.design.features.signboardsDesc,
      readMoreContent: typeof t.design.features.signboards === 'object' ? t.design.features.signboards.readMoreContent : null
    }
  ], [t.design.features]);

  const selectedFeature = selectedCard !== null && features[selectedCard] && features[selectedCard].readMoreContent ? features[selectedCard] : null;

  return (
    <>
      <section id="design" className="design-section">
        <div className="container">
          <div className="design-content">
            <div className="section-header">
              <h2 className="section-title">{t.design.title}</h2>
              <h3 className="section-subtitle">{t.design.subtitle}</h3>
              <p className="section-description design-section-lead">{t.design.description}</p>
            </div>
            <div className="features-grid">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="feature-card"
                  onClick={() => feature.readMoreContent && setSelectedCard(index)}
                >
                  <div className="feature-icon">{feature.icon}</div>
                  <h4 className="feature-title">{feature.title}</h4>
                  {feature.description && (
                    <p className="feature-description">
                      {feature.description}
                      {feature.readMoreContent && (
                        <span className="feature-read-more"> {readMoreText}</span>
                      )}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <div className="solar-lighting-card">
              <h4 className="card-title">{t.design.features.solarLights}</h4>
              <p className="card-text">{t.design.features.solarLightsDesc}</p>
            </div>
            <div className="design-footer">
              <p className="footer-text">{t.design.footer}</p>
            </div>
          </div>
        </div>
      </section>

      {selectedFeature && mounted && createPortal(
        <div className="design-modal-overlay" onClick={handleCloseModal}>
          <div className="design-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="design-modal-close" 
              onClick={() => setSelectedCard(null)} 
              aria-label="Close"
              type="button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18"></path>
                <path d="M6 6l12 12"></path>
              </svg>
            </button>
            <div className="design-modal-body">
              <div className="design-modal-icon">{features[selectedCard].icon}</div>
              <h2 className="design-modal-heading">{selectedFeature.title}</h2>
              <p className="design-modal-text">
                {selectedFeature.description} {selectedFeature.readMoreContent}
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default DesignSafety;
